'use client';

import {
  BodyDefault,
  Button,
  ConfirmationDialog,
  Drawer,
  EmptyState,
  HeadingItem,
  IconButton,
  MetaDefault,
  Spinner,
  VirtualizedStack,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  useAiDiscoveryExecutions,
  useAiDiscoveryRunControls,
  useAiDiscoverySummary,
  useRetryDiscoveryProbeAction,
} from '../../hooks/api/useAiDiscovery';
import { usePermissions } from '../../hooks/usePermissions';
import {
  useIsMcpServerTarget,
  useProjectStore,
} from '../../store/projectStore';
import { useLayoutStore } from '../../store/layoutStore';
import {
  canRetryDiscoveryProbe,
  getAiDiscoveryMetadata,
  shouldShowDiscoveryProtocolColumn,
} from './discovery.utils';
import { resolveIsAiDiscoveryRunning } from './ai-discovery-running.util';
import {
  computeAiDiscoveryStatusBarCounts,
  resolveAiDiscoveryStatusBarBucket,
  type AiDiscoveryStatusBarFilter,
} from './ai-discovery-run-status-bar.util';
import {
  computeMcpDiscoveryStatusBarCounts,
  resolveMcpDiscoveryRuns,
  type McpDiscoveryRun,
} from './mcp-discovery-run.util';
import {
  sortDiscoveryExecutionsNewestFirst,
  sortMcpDiscoveryRunsByTime,
} from './mcp-discovery-run-aggregate.util';
import {
  AiDiscoveryAgentHistoryRow,
  AiDiscoveryMcpHistoryRow,
} from './AiDiscoveryHistoryRows';
import { AiDiscoveryAgentProbeDrawer } from './AiDiscoveryAgentProbeDrawer';
import { AiDiscoveryMcpRunDrawer } from './AiDiscoveryMcpRunDrawer';
import { useAiDiscoveryCanRun } from './useAiDiscoveryCanRun';
import { useAiApplicationComponents } from '../../hooks/api/useAiApplicationComponents';
import { AiDiscoveryCoverage } from './AiDiscoveryCoverage';
import type { AgentExecutionDto } from '@terra/shared-contracts-agent-execution';

const DRAWER_WIDTH = '480px';
const DRAWER_TOP = '10';

type StatusFilter = 'ALL' | AiDiscoveryStatusBarFilter;

type AiDiscoveryHistoryDrawerProps = {
  applicationId: string;
  isOpen: boolean;
  onClose: () => void;
};

export function AiDiscoveryHistoryDrawer({
  applicationId,
  isOpen,
  onClose,
}: AiDiscoveryHistoryDrawerProps) {
  const t = useTranslations('discovery.aiApplication');
  const tCommon = useTranslations('common');
  const { canManageApplications } = usePermissions();
  const isMcpServer = useIsMcpServerTarget();
  const appName = useProjectStore((state) => state.selectedTarget?.name);
  const openTab = useLayoutStore((state) => state.openOrFocusMainContentTab);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [isStopConfirmOpen, setIsStopConfirmOpen] = useState(false);
  const [selectedMcpRun, setSelectedMcpRun] = useState<McpDiscoveryRun | null>(
    null,
  );
  const [selectedAgentExecution, setSelectedAgentExecution] =
    useState<AgentExecutionDto | null>(null);

  const executionsQuery = useAiDiscoveryExecutions({
    applicationId,
    enabled: isOpen && !!applicationId,
    refetchInterval: isOpen ? 5000 : false,
  });
  const discoverySummaryQuery = useAiDiscoverySummary({
    aiApplicationId: applicationId,
    enabled: isOpen && !!applicationId,
    refetchInterval: isOpen ? 5000 : false,
  });
  const { runDiscovery, stopDiscovery, isRunPending, isStopPending } =
    useAiDiscoveryRunControls({
      aiApplicationId: applicationId,
    });
  const { canRun } = useAiDiscoveryCanRun({
    applicationId,
    enabled: isOpen && !!applicationId,
    isRunPending,
  });
  const componentsQuery = useAiApplicationComponents({
    aiApplicationId: applicationId,
    enabled: isOpen,
  });
  const { onRetryProbe, isRetryPending } = useRetryDiscoveryProbeAction({
    aiApplicationId: applicationId,
  });

  const executions = useMemo(
    () => sortDiscoveryExecutionsNewestFirst(executionsQuery.executions),
    [executionsQuery.executions],
  );
  const discoverySummary = discoverySummaryQuery.discoverySummary;
  const isDiscoveryRunning = resolveIsAiDiscoveryRunning({
    executions,
    discoverySummary,
  });
  const mcpRuns = useMemo(
    () =>
      resolveMcpDiscoveryRuns({
        historyEntries: discoverySummary?.discoveryRunHistory ?? [],
        executions,
      }),
    [discoverySummary?.discoveryRunHistory, executions],
  );
  const liveSelectedMcpRun = useMemo(() => {
    if (selectedMcpRun == null) {
      return null;
    }

    return (
      mcpRuns.find((run) => run.id === selectedMcpRun.id) ?? selectedMcpRun
    );
  }, [mcpRuns, selectedMcpRun]);

  const statusBarCounts = useMemo(
    () =>
      isMcpServer
        ? computeMcpDiscoveryStatusBarCounts(mcpRuns)
        : computeAiDiscoveryStatusBarCounts(executions),
    [executions, isMcpServer, mcpRuns],
  );
  const totalCount = isMcpServer ? mcpRuns.length : executions.length;

  const filteredAgentExecutions = useMemo(() => {
    if (statusFilter === 'ALL') {
      return executions;
    }

    return executions.filter(
      (execution) =>
        resolveAiDiscoveryStatusBarBucket(execution) === statusFilter,
    );
  }, [executions, statusFilter]);
  const showAgentProtocol = useMemo(
    () =>
      shouldShowDiscoveryProtocolColumn({
        executions: filteredAgentExecutions,
      }),
    [filteredAgentExecutions],
  );

  const filteredMcpRuns = useMemo(() => {
    const runs =
      statusFilter === 'ALL'
        ? mcpRuns
        : mcpRuns.filter((run) => run.statusBarBucket === statusFilter);

    return sortMcpDiscoveryRunsByTime(runs);
  }, [mcpRuns, statusFilter]);

  const openExecution = useCallback(
    (executionId: string) => {
      const fromList = executionsQuery.executions.find(
        (execution) => execution.id === executionId,
      );

      if (fromList) {
        setSelectedAgentExecution(fromList);

        return;
      }

      openTab({
        id: `agent-execution-${executionId}`,
        title: t('executionTitle'),
        type: 'agent-execution',
        data: { executionId },
      });
      onClose();
    },
    [executionsQuery.executions, onClose, openTab, t],
  );

  const liveSelectedAgentExecution = useMemo(() => {
    if (!selectedAgentExecution) {
      return null;
    }

    return (
      executionsQuery.executions.find(
        (execution) => execution.id === selectedAgentExecution.id,
      ) ?? selectedAgentExecution
    );
  }, [executionsQuery.executions, selectedAgentExecution]);

  const handleAgentProbeRetry = useCallback(
    (candidateId: string) => {
      if (!onRetryProbe) {
        return;
      }

      onRetryProbe(candidateId, {
        onStarted: () => {
          setSelectedAgentExecution(null);
        },
      });
    },
    [onRetryProbe],
  );

  const filterChips: Array<{ value: StatusFilter; label: string }> = [
    {
      value: 'ALL',
      label: t('filterChip', { label: t('filterAll'), count: totalCount }),
    },
    {
      value: 'IN_PROGRESS',
      label: t('filterChip', {
        label: t('filterRunning'),
        count: statusBarCounts.IN_PROGRESS,
      }),
    },
    {
      value: 'COMPLETED',
      label: t('filterChip', {
        label: t('filterComplete'),
        count: statusBarCounts.COMPLETED,
      }),
    },
    {
      value: 'FAILED',
      label: t('filterChip', {
        label: t('filterFailed'),
        count: statusBarCounts.FAILED,
      }),
    },
  ];

  const isLoading = isMcpServer
    ? discoverySummaryQuery.isLoading && discoverySummary == null
    : executionsQuery.isLoading && executions.length === 0;
  const hasRows = isMcpServer
    ? filteredMcpRuns.length > 0
    : filteredAgentExecutions.length > 0;
  const isError = isMcpServer
    ? discoverySummaryQuery.isError && !hasRows && discoverySummary == null
    : executionsQuery.isError && !hasRows;

  return (
    <>
      <Drawer.Root
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            setSelectedMcpRun(null);
            setSelectedAgentExecution(null);
            onClose();
          }
        }}
      >
        <Drawer.Backdrop top={DRAWER_TOP} />
        <Drawer.Positioner
          w={DRAWER_WIDTH}
          top={DRAWER_TOP}
          bottom="0"
          h="auto"
        >
          <Drawer.Content
            display="flex"
            flexDirection="column"
            overflowX="hidden"
            h="full"
          >
            <Drawer.Header px="3" py="3">
              <HStack justifyContent="space-between" w="full" gap="2">
                <VStack alignItems="flex-start" gap="0.5" minW="0">
                  <HeadingItem>{t('historyTitle')}</HeadingItem>
                  {appName ? (
                    <MetaDefault color="fg.muted" truncate>
                      {appName}
                    </MetaDefault>
                  ) : null}
                </VStack>
                <HStack gap="2">
                  {canManageApplications && isDiscoveryRunning ? (
                    <Button
                      size="xs"
                      variant="outline"
                      colorPalette="red"
                      onClick={() => setIsStopConfirmOpen(true)}
                    >
                      {t('stop')}
                    </Button>
                  ) : null}
                  <IconButton
                    variant="ghost"
                    size="xs"
                    aria-label={tCommon('close')}
                    onClick={onClose}
                  >
                    <Cancel01Icon size={16} />
                  </IconButton>
                </HStack>
              </HStack>
            </Drawer.Header>
            <Drawer.Body px="3" py="3" flex="1" minH="0" overflowY="auto">
              <VStack gap="3" alignItems="stretch" h="full">
                <AiDiscoveryCoverage components={componentsQuery.components} />

                <HStack gap="1" flexWrap="wrap">
                  {filterChips.map((chip) => (
                    <Button
                      key={chip.value}
                      size="xs"
                      variant={
                        statusFilter === chip.value ? 'solid' : 'outline'
                      }
                      onClick={() => setStatusFilter(chip.value)}
                    >
                      {chip.label}
                    </Button>
                  ))}
                </HStack>

                {isLoading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flex="1"
                    py="8"
                  >
                    <Spinner size="sm" />
                  </Box>
                ) : null}

                {isError ? (
                  <VStack gap="2" py="6" alignItems="center">
                    <BodyDefault color="fg.error">
                      {tCommon('error')}
                    </BodyDefault>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => {
                        void executionsQuery.refetch();
                        void discoverySummaryQuery.refetch();
                      }}
                    >
                      {tCommon('retry')}
                    </Button>
                  </VStack>
                ) : null}

                {!isLoading && !isError && !hasRows ? (
                  statusFilter === 'ALL' ? (
                    <EmptyState title={t('emptyHistory')} maxWidth="full">
                      {canManageApplications && !isDiscoveryRunning ? (
                        <Button
                          size="xs"
                          variant="outline"
                          loading={isRunPending}
                          disabled={!canRun}
                          onClick={runDiscovery}
                        >
                          {t('runDiscovery')}
                        </Button>
                      ) : null}
                    </EmptyState>
                  ) : (
                    <EmptyState
                      title={t('emptyHistoryFilter')}
                      maxWidth="full"
                    />
                  )
                ) : null}

                {!isLoading && !isError && hasRows && isMcpServer ? (
                  <VirtualizedStack
                    data={filteredMcpRuns}
                    computeItemKey={({ item }) => item.id}
                    renderItem={({ item: run }) => (
                      <AiDiscoveryMcpHistoryRow
                        run={run}
                        onView={() => setSelectedMcpRun(run)}
                      />
                    )}
                  />
                ) : null}

                {!isLoading && !isError && hasRows && !isMcpServer ? (
                  <VirtualizedStack
                    data={filteredAgentExecutions}
                    computeItemKey={({ item }) => item.id}
                    renderItem={({ item: execution }) => (
                      <AiDiscoveryAgentHistoryRow
                        execution={execution}
                        showProtocol={showAgentProtocol}
                        isRetryPending={isRetryPending}
                        onView={() => setSelectedAgentExecution(execution)}
                        onRetry={
                          onRetryProbe && canRetryDiscoveryProbe({ execution })
                            ? () => {
                                const candidateId = getAiDiscoveryMetadata(
                                  execution.metadata,
                                )?.candidateId;

                                if (candidateId) {
                                  onRetryProbe(candidateId);
                                }
                              }
                            : undefined
                        }
                        onStop={
                          canManageApplications &&
                          (execution.status === 'RUNNING' ||
                            execution.status === 'PENDING')
                            ? () => setIsStopConfirmOpen(true)
                            : undefined
                        }
                      />
                    )}
                  />
                ) : null}
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>

      <AiDiscoveryMcpRunDrawer
        run={liveSelectedMcpRun}
        executions={executions}
        onClose={() => setSelectedMcpRun(null)}
        onOpenExecution={openExecution}
        onRetryProbe={onRetryProbe}
      />

      <AiDiscoveryAgentProbeDrawer
        execution={liveSelectedAgentExecution}
        onClose={() => setSelectedAgentExecution(null)}
        onRetryProbe={onRetryProbe ? handleAgentProbeRetry : undefined}
        isRetryPending={isRetryPending}
      />

      <ConfirmationDialog
        isOpen={isStopConfirmOpen}
        onOpenChange={({ open }) => {
          if (!open) {
            setIsStopConfirmOpen(false);
          }
        }}
        onConfirm={() => {
          stopDiscovery({
            onStopped: () => {
              setIsStopConfirmOpen(false);
            },
          });
        }}
        loading={isStopPending}
        variant="destructive"
        title={t('stopConfirmTitle')}
        description={t('stopConfirmDescription')}
        confirmText={t('stop')}
        cancelText={tCommon('cancel')}
      />
    </>
  );
}
